import { useState } from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import dayjs from 'dayjs'
import { useRouteQuery, useTranslator } from '@/hooks'
import { BalanceRecord } from '@/@data/balance'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { ICompany } from '@/@data'
import { BalanceFilterPastTable } from './BalanceFilterPastTable'
import { getInitials } from '@/utils/getInitials'

interface BalancePastTableProps {
  data: BalanceRecord[]
  meta: unknown
  setOpenPastStatistic: () => void
  openPastStatistic: boolean
  refetch: () => void
}

export const BalancePastTable = ({
  data,
  meta,
  setOpenPastStatistic,
  openPastStatistic,
  refetch
}: BalancePastTableProps) => {
  const { __, __n } = useTranslator()
  const { searchQuery } = useRouteQuery()
  const navigate = useNavigate()

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={34} />
    } else {
      return (
        <CustomAvatar skin='light' size={34}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  const monthParam = searchQuery.get('month') || dayjs().startOf('month').format('YYYY-MM-DD')

  const COLUMN_KEYS = {
    EMPLOYEE: 'employee',
    STATUS: 'status',
    DIFFERENT: 'different',
    PAY_DIFFERENT: 'pay_different',
    TOTAL_EWA: 'total_ewa_disbursement',
    EXPIRED_CONTRACT: 'expired_contract',
    TOTAL_TRANSACTION_FEES: 'total_transaction_fees',
    VOUCHER: 'voucher',
    CUMULATIVE_CURRENT_DEBT: 'cumulative_current_debt',
    SERVICES_FEE: 'services_fee'
  }

  const columnMap = [
    { key: COLUMN_KEYS.EMPLOYEE, label: __('Employee') },
    { key: COLUMN_KEYS.STATUS, label: __('Status') },
    { key: COLUMN_KEYS.DIFFERENT, label: __('Different') },
    { key: COLUMN_KEYS.EXPIRED_CONTRACT, label: __('Expired Contract') },
    { key: COLUMN_KEYS.PAY_DIFFERENT, label: __('Pay Different') },
    { key: COLUMN_KEYS.TOTAL_EWA, label: __('Total EWA Disbursement') },
    { key: COLUMN_KEYS.TOTAL_TRANSACTION_FEES, label: __('Total Transaction Fees') },
    { key: COLUMN_KEYS.VOUCHER, label: __('Voucher') },
    { key: COLUMN_KEYS.CUMULATIVE_CURRENT_DEBT, label: __('Cumulative Current Debt') },
    { key: COLUMN_KEYS.SERVICES_FEE, label: __('Services Fee') }
  ]

  const [visibleColumns, setVisibleColumns] = useState<string[]>(columnMap.map(c => c.key))
  const isColumnVisible = (columnKey: string) => visibleColumns.includes(columnKey)

  return (
    <>
      <BalanceFilterPastTable
        onStatistic={setOpenPastStatistic}
        openStatistic={openPastStatistic}
        visibleColumns={visibleColumns}
        columns={columnMap}
        onSelectColumns={cols => {
          setVisibleColumns(cols)
        }}
        refetch={refetch}
      />
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e0e0e0' }}>
          <thead>
            <tr className='border-right-custom'>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase'
                }}
              >
                {__('Employer')}
              </th>
              {isColumnVisible(COLUMN_KEYS.EMPLOYEE) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Employee')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.STATUS) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Status')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.DIFFERENT) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Different')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.TOTAL_EWA) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Total EWA Disbursement')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.PAY_DIFFERENT) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Pay Different')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.EXPIRED_CONTRACT) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Expired Contract')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.TOTAL_TRANSACTION_FEES) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Total Transaction Fees')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.VOUCHER) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Voucher')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.CUMULATIVE_CURRENT_DEBT) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Cumulative Current Debt')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.SERVICES_FEE) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Services Fee')}
                </th>
              )}
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase'
                }}
              >
                {__('Action')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, index) => {
                const status = (row as any).status

                const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
                  paid: { label: 'Paid', color: '#28C76F', bgColor: '#E8F5E8' },
                  partial: { label: 'Pay Partial', color: '#45BAD1', bgColor: '#D6F4F8' },
                  unpaid: { label: 'Unpaid', color: '#EA5455', bgColor: '#FFEBEE' },
                  pending: { label: 'Pending', color: '#F69F43', bgColor: '#FDF0E1' }
                }

                const config = statusConfig[status]
                const different = (row as any)?.different
                const isPositive = row?.request_amount < row?.eligibility_amount
                const isNegative = row?.request_amount >= row?.eligibility_amount
                const voucherAmount = parseFloat(String(row.voucher_discount || 0))

                return (
                  <tr key={index}>
                    <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                      <div
                        onClick={() => navigate(`/cms/finance/balance/${row?.company_id}?month=${monthParam}`)}
                        className='flex items-center gap-4 no-underline cursor-pointer'
                      >
                        {getAvatar({ logo_file: row.company?.logo_file, name: row.company?.name || '-' })}
                        <div className='flex flex-col'>
                          <Typography
                            className='font-medium max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis text-bold'
                            color='text.primary'
                          >
                            {row.company?.name || '-'}
                          </Typography>
                          <Typography variant='body2'>{row.company?.system_code || '-'}</Typography>
                        </div>
                      </div>
                    </td>

                    {isColumnVisible(COLUMN_KEYS.EMPLOYEE) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <div className='flex items-center gap-2'>
                          <i className='tabler-users text-[#3551F2]' />
                          <Typography className='text-[15px] leading-[22px] font-normal'>
                            {(row as any)?.company?.total_user}
                          </Typography>
                        </div>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.STATUS) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Chip
                          label={__(config.label)}
                          size='small'
                          sx={{
                            backgroundColor: config.bgColor,
                            color: config.color,
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500
                          }}
                        />
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.DIFFERENT) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        {row?.status === 'pending' ? (
                          '-'
                        ) : (
                          <Typography
                            className={`text-[15px] leading-[22px] font-normal ${different === 0 ? 'text-gray-500' : isPositive ? 'text-[#28C76F]' : isNegative ? 'text-[#EA5455]' : 'text-gray-500'}`}
                          >
                            {__n(Math.abs(different), 'đ')}
                          </Typography>
                        )}
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.TOTAL_EWA) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {Number(row?.request_amount) > 0 ? __n(row?.request_amount, 'đ') : '-'}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.PAY_DIFFERENT) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        {row?.status === 'pending' || row?.status === 'unpaid' || isPositive ? (
                          '-'
                        ) : (
                          <div className='flex flex-col gap-2'>
                            <Typography className={`text-[15px] leading-[22px] font-medium`}>
                              {__n(row?.cus_pay_diff, 'đ')}
                            </Typography>
                            <Typography className={`text-[15px] leading-[22px] font-normal`}>
                              {__('Lift')}: {__n(row?.lift_paid, 'đ')}
                            </Typography>
                          </div>
                        )}
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.EXPIRED_CONTRACT) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {row.contract ? dayjs(row.contract?.end_date).format('DD/MM/YYYY') : '-'}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.TOTAL_TRANSACTION_FEES) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {Number(row.service_fee) > 0 ? __n(row.service_fee, 'đ') : '-'}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.VOUCHER) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Box className='flex items-center gap-2'>
                          {voucherAmount > 0 && (
                            <Chip
                              label={`${__n(voucherAmount, 'đ')}`}
                              size='small'
                              sx={{
                                backgroundColor: '#E8E6FC',
                                color: '#3551F2',
                                borderRadius: '12px',
                                fontSize: '12px'
                              }}
                            />
                          )}
                          <Typography className='text-[15px] leading-[22px] font-normal'>
                            {__n(voucherAmount, 'đ')}
                          </Typography>
                        </Box>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.CUMULATIVE_CURRENT_DEBT) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n(row.cumulative_debt_data, 'đ')}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.SERVICES_FEE) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {Number(row.service_fee) > 0 ? __n(row.service_fee, 'đ') : '-'}
                        </Typography>
                      </td>
                    )}

                    <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                      <div
                        onClick={() => navigate(`/cms/finance/balance/${row?.company_id}?month=${monthParam}`)}
                        className='flex items-center gap-4 no-underline cursor-pointer'
                      >
                        <i className='tabler-external-link text-[#2F2B3DB2] text-[18px]' />
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td
                  style={{ padding: '24px 16px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}
                  colSpan={2 + visibleColumns.length}
                >
                  <Typography className='text-[15px] leading-[22px] font-normal text-gray-500'>
                    {__('No Data')}
                  </Typography>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      {meta && (
        <TablePagination
          meta={
            {
              ...(meta as any),
              page: Number(searchQuery.get('page')) || 1
            } as unknown as TablePaginationProps
          }
        />
      )}
    </>
  )
}
